import { useEffect } from "react";
import { MapContainer, Marker, TileLayer, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import { BASEMAPS, DIRECTORY_DEFAULT_BASEMAP, readBasemap, resolveBasemap } from "../lib/basemap";

/**
 * Issue #1152 (D225 item 9) — both pins of a duplicate pair on one small map.
 *
 * The merge is the most destructive verb on `/admin/grounds`, and the old
 * footnote sent the reviewer to the whole grounds map to find the two pins by
 * hand. This draws the pair itself: marker A, marker B, fitted so both are in
 * view. It is the directory's basemap and nothing else: no layer switcher, no
 * drawing, no click handlers. Zoom and attribution stay (D224 does not count
 * them as choices), and the tile credit is the source's own (`lib/basemap.js`).
 */
const FIT_PADDING = 24;
const FIT_MAX_ZOOM = 17;

const isPoint = (p) => Number.isFinite(Number(p?.lat)) && Number.isFinite(Number(p?.lng)) && p?.lat !== null && p?.lng !== null;

const letterIcon = (letter) =>
  new L.DivIcon({
    className: "pair-map-marker",
    html: `<div style="width:26px;height:26px;border-radius:50%;background:#0B2E59;color:#fff;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;font:700 13px/1 sans-serif;">${letter}</div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 13],
  });

const ICON_A = letterIcon("A");
const ICON_B = letterIcon("B");

/** `MapContainer`'s bounds only apply at first mount; this fits once both pins exist. */
function PairFitter({ a, b }) {
  const map = useMap();
  useEffect(() => {
    map.fitBounds(
      L.latLngBounds([
        [Number(a.lat), Number(a.lng)],
        [Number(b.lat), Number(b.lng)],
      ]),
      { padding: [FIT_PADDING, FIT_PADDING], maxZoom: FIT_MAX_ZOOM },
    );
  }, [map, a.lat, a.lng, b.lat, b.lng]);
  return null;
}

/**
 * #1176 — Tab stops. Order here: the map (one stop; Leaflet makes its
 * container focusable and +/- and the arrow keys zoom and pan it), then the
 * basemap's credit link, then "Not a duplicate". Leaflet would add a stop
 * for each zoom button and one for its own "Leaflet" prefix link, so one
 * card cost seven. The zoom buttons leave the tab order (they stay on
 * screen and clickable; +/- on the map zoom it). The prefix link is dropped.
 * The basemap's credit link stays a stop: `lib/basemap.js` trims the Esri
 * credit to that link on purpose, so the full credit list is reachable only
 * through it.
 */
const ZOOM_LINKS = ".leaflet-control-zoom a";

function MapKeyboardStops({ label }) {
  const map = useMap();
  useEffect(() => {
    const container = map.getContainer();
    container.setAttribute("role", "region");
    container.setAttribute("aria-label", label);
    map.attributionControl?.setPrefix(false);
    const controls = container.querySelector(".leaflet-control-container") || container;
    const apply = () => controls.querySelectorAll(ZOOM_LINKS).forEach((el) => el.setAttribute("tabindex", "-1"));
    apply();
    const observer = new MutationObserver(apply);
    observer.observe(controls, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [map, label]);
  return null;
}

export default function PairMap({ a, b, height = 180 }) {
  if (!isPoint(a) || !isPoint(b)) return null;
  const basemap = BASEMAPS.find((m) => m.id === resolveBasemap(readBasemap(), DIRECTORY_DEFAULT_BASEMAP));
  const posA = [Number(a.lat), Number(a.lng)];
  const posB = [Number(b.lat), Number(b.lng)];
  return (
    <div
      className="w-full"
      style={{ height, borderRadius: "var(--nw-radius-card)", overflow: "hidden", border: "1px solid var(--nw-line)" }}
      data-testid="dupe-pair-map"
    >
      <MapContainer center={posA} zoom={15} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
        <TileLayer attribution={basemap.attribution} url={basemap.url} maxZoom={basemap.maxZoom} noWrap />
        <PairFitter a={a} b={b} />
        <MapKeyboardStops label={`Map of ${a.name ?? "record A"} and ${b.name ?? "record B"}. Press plus or minus to zoom.`} />
        {/* keyboard={false}: the pins are not Tab stops. They open nothing
            and a marker has no accessible name of its own; the record links
            above the map open each record (#1176). */}
        <Marker position={posA} icon={ICON_A} keyboard={false}>
          <Tooltip direction="top" offset={[0, -12]}>{`A: ${a.name ?? ""}`}</Tooltip>
        </Marker>
        <Marker position={posB} icon={ICON_B} keyboard={false}>
          <Tooltip direction="top" offset={[0, -12]}>{`B: ${b.name ?? ""}`}</Tooltip>
        </Marker>
      </MapContainer>
    </div>
  );
}
